import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaChartBar, FaBoxOpen, FaCubes, FaStore, FaUserPlus, FaTicketAlt } from "react-icons/fa";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import FirebaseApp from "../config/Firebase";

export default function Middle() {
  const navigate = useNavigate();
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUserBranch = async () => {
      try {
        const username = localStorage.getItem('currentUsername');
        if (!username) {
          setLoading(false);
          return;
        }

        const db = getFirestore(FirebaseApp);
        const userDoc = await getDoc(doc(db, "LDB", username));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // If branch field exists and is truthy, hide Create Admin; else show it
          setShowCreateAdmin(!userData.branch);
        } else {
          setShowCreateAdmin(true); // Default to show if no doc
        }
      } catch (error) {
        console.error("Error checking user branch:", error);
        setShowCreateAdmin(true); // Default to show on error
      } finally {
        setLoading(false);
      }
    };

    checkUserBranch();
  }, []);

  const dashboardItems = [
    {
      title: "Add Product",
      icon: <FaPlus className="text-3xl" />,
      path: "/AddProduct",
    },
    {
      title: "Add Coopound",
      icon: <FaTicketAlt className="text-3xl" />,
      path: "/AddCoopound",
    },
    {
      title: "View Analysis",
      icon: <FaChartBar className="text-3xl" />,
      path: "/viewanalysis",
    },
    {
      title: "Orders",
      icon: <FaBoxOpen className="text-3xl" />,
      path: "/Orders",
    },
    {
      title: "Inventory Management",
      icon: <FaCubes className="text-3xl" />,
      path: "/InventoryManagement",
    },
    {
      title: "Store Management",
      icon: <FaStore className="text-3xl" />,
      path: "/StoreManagement",
    },
  ];

  // Conditionally add Create Admin
  if (showCreateAdmin) {
    dashboardItems.push({
      title: "Create Admin",
      icon: <FaUserPlus className="text-3xl" />,
      path: "/CreateAdmin",
    });
  }

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  // Animation for the header
  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Animation for the new bar
  const barVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut", delay: 0.4 },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
      {/* Header */}
      <motion.h1
        variants={headerVariants}
        initial="hidden"
        animate="visible"
        className="text-4xl sm:text-5xl font-extrabold text-black mb-10 relative"
      >
        Admin Dashboard
        <span className="absolute bottom-0 left-0 w-1/3 h-1 bg-black rounded-full" />
      </motion.h1>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl w-full">
        <AnimatePresence>
          {dashboardItems.map((item, index) => (
            <motion.div
              key={item.title}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              whileTap="tap"
              className="relative group p-6 rounded-xl bg-black text-white shadow-md cursor-pointer overflow-hidden"
              onClick={() => navigate(item.path)}
            >
              {/* Icon and Title */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">{item.icon}</div>
                <h2 className="text-xl font-semibold">{item.title}</h2>
              </div>

              {/* Hover Overlay Effect */}
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />

              {/* Subtle Border Animation */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 rounded-xl transition-all duration-300" />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* New Full-Width Bar */}
      <motion.div
        variants={barVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-6xl mt-6 p-4 bg-black text-white rounded-xl shadow-md flex items-center justify-center cursor-pointer"
        onClick={() => navigate("/Visitor")}
      >
        <h2 className="text-lg font-semibold">Visitor management</h2>
      </motion.div>
    </div>
  );
}